/**
 * Wishlist Page
 * Display and manage saved products
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Container, Main, Flex } from '../../../components/styled/Layout';
import { Card, CardContent } from '../../../components/styled/Card';
import { PrimaryButton, SecondaryButton, DangerButton } from '../../../components/styled/Button';
import { useAuth } from '../../../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import wishlistService from '../services/wishlistService';
import productService from '../services/productService';
import useShopFeatures from '../hooks/useShopFeatures';
import { getRatingValue, getRatingCount, renderStars, formatPrice } from '../utils/productHelpers';
import './Shop.css';

const Wishlist = () => {
  const [wishlistItems, setWishlistItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const { isAuthenticated } = useAuth();
  const { addItem: addToCart } = useCart();
  const navigate = useNavigate();
  const features = useShopFeatures();

  useEffect(() => {
    loadWishlist();
  }, [isAuthenticated]);

  const loadWishlist = async () => {
    try {
      setLoading(true);
      
      if (isAuthenticated) {
        // Load from backend
        const backendWishlist = await wishlistService.getWishlist();
        
        // Fetch full product details for each wishlist item
        const itemsWithProducts = await Promise.all(
          backendWishlist.map(async (item) => {
            const product = await productService.getProduct(item.productId);
            return {
              ...item,
              product
            };
          })
        );
        
        setWishlistItems(itemsWithProducts);
      } else {
        // Load from localStorage
        const localWishlist = wishlistService.getLocalWishlist();
        setWishlistItems(localWishlist);
      }
    } catch (error) {
      console.error('Error loading wishlist:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async (productId) => {
    try {
      if (isAuthenticated) {
        await wishlistService.removeFromWishlist(productId);
      } else {
        wishlistService.removeFromLocal(productId);
      }
      
      // Update local state
      setWishlistItems(items => items.filter(item => item.product?.id !== productId && item.id !== productId));
    } catch (error) {
      console.error('Error removing from wishlist:', error);
      alert('Failed to remove item from wishlist');
    }
  };

  const handleAddToCart = (product) => {
    if (product.stockQty > 0) {
      addToCart(product);
      alert(`Added ${product.name} to cart!`);
    }
  };

  const handleClearWishlist = async () => {
    if (!confirm('Are you sure you want to clear your entire wishlist?')) return;

    try {
      if (isAuthenticated) {
        // Clear from backend
        for (const item of wishlistItems) {
          await wishlistService.removeFromWishlist(item.productId || item.product?.id);
        }
      } else {
        wishlistService.clearLocal();
      }
      
      setWishlistItems([]);
    } catch (error) {
      console.error('Error clearing wishlist:', error);
      alert('Failed to clear wishlist');
    }
  };

  if (loading) {
    return (
      <Container>
        <Main>
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading wishlist...</p>
          </div>
        </Main>
      </Container>
    );
  }

  if (wishlistItems.length === 0) {
    return (
      <Container>
        <Main>
          <div className="empty-state">
            <h2>💝 Your wishlist is empty</h2>
            <p>Save products you love for later!</p>
            <PrimaryButton as={Link} to="/shop/catalog">
              Browse Products
            </PrimaryButton>
          </div>
        </Main>
      </Container>
    );
  }

  return (
    <Container>
      <Main>
        <div className="shop-page wishlist-page">
          <Flex justify="space-between" align="center" style={{ marginBottom: '24px' }}>
            <h1>My Wishlist ({wishlistItems.length} items)</h1>
            <DangerButton onClick={handleClearWishlist}>
              Clear Wishlist
            </DangerButton>
          </Flex>

          <div className="product-grid">
            {wishlistItems.map((item) => {
              const product = item.product || item;
              
              return (
                <Card key={product.id} className="wishlist-item-card">
                  <div className="product-image-container" onClick={() => navigate(`/shop/product/${product.id}`)}>
                    <img src={product.image} alt={product.name} className="product-image" />
                  </div>
                  <CardContent>
                    <h3 
                      className="product-name"
                      onClick={() => navigate(`/shop/product/${product.id}`)}
                      style={{ cursor: 'pointer' }}
                    >
                      {product.name}
                    </h3>
                    
                    {product.rating && (
                      <div className="product-rating">
                        <span className="stars">
                          {renderStars(product.rating)}
                        </span>
                        {getRatingCount(product.rating, product.ratingCount) > 0 && (
                          <span className="rating-count">({getRatingCount(product.rating, product.ratingCount)})</span>
                        )}
                      </div>
                    )}

                    <p className="price">${formatPrice(product.price)}</p>

                    {features.stockIndicators && product.stockQty !== undefined && (
                      <div className={`stock-badge ${product.stockQty > 0 ? 'in-stock' : 'out-of-stock'}`}>
                        {product.stockQty > 0 ? `${product.stockQty} in stock` : 'Out of stock'}
                      </div>
                    )}

                    {item.addedAt && (
                      <p className="added-date">
                        Added {new Date(item.addedAt).toLocaleDateString()}
                      </p>
                    )}

                    <Flex gap="8px" direction="column" style={{ marginTop: '12px' }}>
                      <PrimaryButton 
                        onClick={() => handleAddToCart(product)}
                        disabled={product.stockQty === 0}
                        style={{ width: '100%' }}
                      >
                        {product.stockQty > 0 ? 'Add to Cart' : 'Out of Stock'}
                      </PrimaryButton>
                      <DangerButton 
                        onClick={() => handleRemove(product.id)}
                        size="small"
                        style={{ width: '100%' }}
                      >
                        Remove from Wishlist
                      </DangerButton>
                    </Flex>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </Main>
    </Container>
  );
};

export default Wishlist;

